import { Select } from 'antd';
import { useRankOptions } from '@/hooks/useRankOptions';
import { withChosenOptions } from '@/lib/selectOptions';

/**
 * A MULTI-select over the rank master.
 *
 * ⚠️ Page-local, and not a `mode` prop on `RankPicker`. Cardinality is not a mode: this
 * has a different `onChange` signature, a different `allowClear` meaning and a different
 * placeholder, and folding it into `RankPicker` would force a discriminated props union
 * plus a cast at the AntD boundary onto a component four call sites already depend on.
 * Both read `useRankOptions`, so there is still exactly one query.
 *
 * It moves up to `components/` the moment a second page needs it — the same rule
 * `pages/Places/components/PlacePicker.tsx` writes for itself.
 *
 * ⚠️ ORDER IS PRESERVED. AntD's multi-select reports values in SELECTION order, and that
 * is what the model stores, so the editor shows an administrator their own arrangement
 * back rather than a re-sorted one.
 */
export const RankMultiPicker = ({
  value,
  onChange,
  disabled,
}: {
  value?: string[];
  onChange?: (value: string[]) => void;
  disabled?: boolean;
}) => {
  const { options, resolve, isFetching } = useRankOptions();

  return (
    <Select
      mode="multiple"
      value={value ?? []}
      onChange={(next: string[]) => onChange?.(next)}
      loading={isFetching}
      disabled={disabled}
      showSearch
      optionFilterProp="label"
      placeholder="Which ranks may fill this post?"
      className="w-full"
      data-testid="rank-multi-picker"
      /* ⚠️ [D4.5 §1b] A post type saved with a rank that has since been deactivated keeps
         that rank's name on its tag instead of showing an ObjectId — `lib/selectOptions.ts`.
         The deactivated row is not offered to anyone editing the list. */
      options={withChosenOptions(options, value ?? [], resolve)}
    />
  );
};
